import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, Box, Typography, Divider, Skeleton } from '@mui/material';
import { HighChartDonutChart, AreaChart } from '../../../shared';
import {
    TRIGGER_ENGAGEMENT_CHART_OPTIONS,
    CREATIVE_NAME_CHART_OPTIONS,
} from './constants';
import { useSelector } from 'react-redux';
import useChartOptions from '../../../../utility/hooks/useChartOptions';

const engagementRateTitles = {
    64: 'Engagement Rate',
    66: 'Engagement Rate',
    161: 'Engagement Rate',
    default: 'Trigger Engagement Rate',
};

const areaChartTitles = {
    64: 'Audience Count',
    66: 'Audience Count',
    161: 'Audience Count',
    default: 'Trigger Count',
};

const areaChartYaxis = {
    64: 'Count',
    66: 'Count',
    161: 'Count',
    default: 'Trigger Count',
};

const TriggerEngagement = (props) => {
    const { trigger } = useParams();
    const { company } = useSelector((state) => state.company);
    const {
        triggerSummary: { engagement, creative, isTriggerSummaryGraphsLoading },
    } = useSelector((state) => state.trigger);
    const [areaData, setAreaData] = useState([
        {
            name: 'Trigger Count',
            data: [],
        },
    ]);
    const powerBi_companies = [41, 51, 53, 59, 72, 90, 92];
    const POWER_BI_TRIGGER_URL = {
        /** ------------------- MULTIPLE MYELOMA ------------------- */
        'Dr Community Oncologist':
            'https://app.powerbi.com/view?r=eyJrIjoiZDkzOTI3N2QtNzE2MC00ZDEzLWEwODYtZjNmZTdiNWU5YjQ4IiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',
        'NP-PA':
            'https://app.powerbi.com/view?r=eyJrIjoiZjIwN2U0MDQtZDk2Yi00NGZiLThkY2QtNjM3NDI0MjZkZDhhIiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',

        /** ------------------- HIGH CHOLESTEROL ------------------- */
        'Decoding HOFH':
            'https://app.powerbi.com/view?r=eyJrIjoiMWFhMThlOTYtZTA2OC00MTk2LTk1MGMtNGQ1YmY2NzJhY2VlIiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',

        /** ------------------- SCREEN KIDS ------------------- */
        'San Anotonio':
            'https://app.powerbi.com/view?r=eyJrIjoiZDVjMGRhYjItNDk4OS00NWQ4LTg0MjAtOGMyODA1YTU1NTlkIiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',

        /** ------------------- SKIN CANCER ------------------- */
        CSCC: 'https://app.powerbi.com/view?r=eyJrIjoiZThiNDMwYzEtMGJhNi00MjdiLTgwNTMtY2VjZGVmMWEwZGRmIiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',

        /** ------------------- DB OTO ------------------- */
        Audiologist:
            'https://app.powerbi.com/view?r=eyJrIjoiYzE0NThiMDQtMmJkOS00YTJlLWJiODItYjQ4YzQ0Y2E2NWFkIiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',
        ENTs: 'https://app.powerbi.com/view?r=eyJrIjoiMzc5MmZiN2UtNDBiOC00MTAzLWI4ODAtMjcxYjYxNGZjZjdjIiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',
        'Otology & Neurotology':
            'https://app.powerbi.com/view?r=eyJrIjoiNDVhZDU3MzAtNzU3Yi00NWJjLWE3M2EtMzRlZGIyZTk3MWU4IiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',
        'Pediatric ENTs':
            'https://app.powerbi.com/view?r=eyJrIjoiM2YxMDIwMDAtMmEwOS00MjA4LWJiNzItNjMyNjZjZWVhOWUxIiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',
        'Pediatric Geneticists':
            'https://app.powerbi.com/view?r=eyJrIjoiYTRlNzJmNDktMTM1NC00NzEwLWJjMGMtYWJjNGYyMmQ1MTE1IiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',
        'Pediatric NP/PA':
            'https://app.powerbi.com/view?r=eyJrIjoiNDU1ZWJlNGYtZWY4Yy00NTllLWJkNWEtZmNlMTM4YzUwZDI4IiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',
        Pediatrics:
            'https://app.powerbi.com/view?r=eyJrIjoiOTE4MGJmMGYtZTg0ZC00NDEyLThiNjEtZGU0MzlhMjE5NWEyIiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',

        /** ------------------- DB OTO 2026 ------------------- */
        'Audiologist 2026':
            'https://app.powerbi.com/view?r=eyJrIjoiNTdlZjU5NzctMjkzNS00NGJkLTk2N2UtN2VmYWM5NDFhMjAzIiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',
        'Pediatricians 2026':
            'https://app.powerbi.com/view?r=eyJrIjoiZDIzZTcyYWYtZGQxMC00NzNkLWEwYWQtYmU3OGZmMzcwZjIxIiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',
        'ENTs 2026':
            'https://app.powerbi.com/view?r=eyJrIjoiNWExMWEwYTgtZThjMy00MWJiLTk5NmItZTg1MzM4NjZiOTdkIiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',
        'Otology & Neurotology 2026':
            'https://app.powerbi.com/view?r=eyJrIjoiNmMyN2Q4ZWUtNWNhNy00YjY0LWE0ZjktNzE2OTcyOGNjOWFmIiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',
        'Pediatric ENTs 2026':
            'https://app.powerbi.com/view?r=eyJrIjoiYmU5YTcwZjUtZjU5Yy00OTgxLWE0M2UtMDlmMjM3YzFjZDA2IiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',
        'Pediatric NP/PA 2026':
            'https://app.powerbi.com/view?r=eyJrIjoiYTRkYTJiZDktM2E2NC00OTQxLWJiNzAtNTkwNTdhNTg2N2I1IiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',
        'Geneticists 2026':
            'https://app.powerbi.com/view?r=eyJrIjoiMDk5ODU2NDEtOTk3MS00YjBjLWI4NWEtMTQ1YmVmNGY5ODE2IiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',
        'Neurodevelopmental Disabilities 2026':
            'https://app.powerbi.com/view?r=eyJrIjoiNmMyN2Q4ZWUtNWNhNy00YjY0LWE0ZjktNzE2OTcyOGNjOWFmIiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',

        /** ------------------- RVO ------------------- */
        Ophthalmology:
            'https://app.powerbi.com/view?r=eyJrIjoiNDI2NzM1MTItNGQ5YS00MWE5LTk2N2UtYjJiOGQwYzc5NDE4IiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',
        Optometrist:
            'https://app.powerbi.com/view?r=eyJrIjoiZDJlYThmNWMtZDNjOC00YWI1LWE5ZWEtNTA2Yzk2MjY1YzY3IiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',
        'Pediatric Ophthalmology':
            'https://app.powerbi.com/view?r=eyJrIjoiNjAwNTQwMGYtYmM0NC00ODQ0LTlkYWMtMDM4OWYwZGZmZWY3IiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',
        'Retinal Specialist':
            'https://app.powerbi.com/view?r=eyJrIjoiOTc1YmZhN2YtZWU1Yy00Y2NjLWFiZDctY2ZmNjg4ZTlhOWE5IiwidCI6IjQxMGYxMWYwLTUzYjgtNDAzNi1hZTdkLWI1ZDkwYmVkY2MzMiIsImMiOjN9',
    };

    const handlePowerBiUrl = (triggerName) =>
        POWER_BI_TRIGGER_URL[triggerName] || '';

    const powerBiUrl = handlePowerBiUrl(trigger);
    const yAxisTitle = areaChartYaxis[company.id] || areaChartYaxis.default;

    const areaChartOptions = useChartOptions(
        CREATIVE_NAME_CHART_OPTIONS,
        { yaxis: { title: { text: yAxisTitle } } },
        [company.id],
    );

    useEffect(() => {
        setAreaData([
            {
                ...creative[0],
                name: yAxisTitle,
            },
        ]);
    }, [creative, yAxisTitle]);

    return (
        <Grid item xs={12}>
            <Grid container spacing={'24px'}>
                {/* Trigger Engagement */}
                <Grid item className="custom-grid-item" xs={4}>
                    <Box
                        className="custom-paper bg-gray-50 padding-24px br-16px min-height-388px"
                        sx={{ maxWidth: '350px' }}
                    >
                        <Box className="flex-row-space-between">
                            <Box>
                                <Typography className="text-md-medium gray-900">
                                    {engagementRateTitles[company.id] ||
                                        engagementRateTitles.default}
                                </Typography>
                            </Box>
                        </Box>
                        {/* HighChartDonutChart */}
                        {isTriggerSummaryGraphsLoading ? (
                            <Box className="box-sizing-border-box pt-20px">
                                <Skeleton
                                    animation="pulse"
                                    height={250}
                                    variant="circular"
                                    width={250}
                                />
                            </Box>
                        ) : (
                            <HighChartDonutChart
                                chartOptions={TRIGGER_ENGAGEMENT_CHART_OPTIONS}
                                seriesData={engagement}
                            />
                        )}
                    </Box>
                </Grid>

                {/* Trigger Count */}
                <Grid item className="custom-grid-item" xs={8}>
                    <Box className="custom-paper bg-white br-16px border-1-gray min-height-388px height-100">
                        {powerBi_companies.includes(company.id) ? (
                            <Box className="padding-24px min-height-320px">
                                <iframe
                                    allowFullScreen={true}
                                    frameBorder={0}
                                    height="400px"
                                    src={powerBiUrl}
                                    title="Power Bi Trigger"
                                    width="100%"
                                />
                            </Box>
                        ) : (
                            <>
                                <Box className="padding-tb-24-rl-20">
                                    <Typography className="text-md-medium gray-900">
                                        {areaChartTitles[company.id] ||
                                            areaChartTitles.default}
                                    </Typography>
                                </Box>
                                <Divider className="border-1-gray" />
                                <Box
                                    className="padding-24px min-height-320px"
                                    sx={{ height: 'calc(100% - 73px)' }}
                                >
                                    {/* Area Chart */}
                                    {isTriggerSummaryGraphsLoading ? (
                                        <Skeleton
                                            animation="wave"
                                            height={320}
                                            variant="rounded"
                                            width={600}
                                        />
                                    ) : (
                                        <AreaChart
                                            chartOptions={areaChartOptions}
                                            chartSeries={areaData}
                                        />
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default TriggerEngagement;
